import { useState } from 'react'
import { PiMagnifyingGlass } from 'react-icons/pi'
import Checkbox from 'components/Checkbox/checkbox'

export type FilterOption = { value: string; label: string }

/**
 * The searchable, checkbox value list behind every multi-value filter: a search
 * box (long lists like owners), a single Select all / Deselect all toggle, and
 * the checkable options. Self-contained body — no trigger, no popover — so it
 * drops straight into whatever surface owns it: the builder sidebar's
 * FilterSelect (which wraps it in its own popover) or the quick-bar filter
 * pill's edit popover. An empty selection means "no filter" (Any).
 *
 * @param label - field label, used in the search placeholder
 * @param options - the selectable values
 * @param selected - currently-checked values
 * @param onChange - called with the next selection
 * @param idPrefix - namespaces the checkbox ids so multiple lists can coexist
 */
export function MultiSelectList({
  label,
  options,
  selected,
  onChange,
  idPrefix,
}: {
  label: string
  options: FilterOption[]
  selected: string[]
  onChange: (next: string[]) => void
  idPrefix: string
}) {
  const [query, setQuery] = useState('')
  const selectedSet = new Set(selected)
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options

  const toggle = (value: string) =>
    onChange(
      selectedSet.has(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    )

  // One state-aware toggle: all selected → clear, otherwise → select all.
  const allSelected = options.length > 0 && selected.length === options.length
  const toggleAll = () =>
    onChange(allSelected ? [] : options.map((o) => o.value))

  return (
    <>
      <div className="border-b border-line p-2">
        <div className="flex items-center gap-2 rounded-md border border-line px-2">
          <PiMagnifyingGlass size={14} className="shrink-0 text-fg-secondary" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}…`}
            aria-label={`Search ${label}`}
            className="h-8 w-full bg-transparent text-sm text-fg outline-none placeholder:text-fg-secondary"
          />
        </div>
        <div className="mt-1 flex items-center px-1">
          <button
            type="button"
            onClick={toggleAll}
            className="text-xs font-medium text-brand-active hover:underline"
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        </div>
      </div>
      <div className="flex max-h-56 flex-col overflow-y-auto p-1">
        {filtered.length === 0 ? (
          <p className="px-2 py-3 text-center text-sm text-fg-secondary">
            No matches
          </p>
        ) : (
          filtered.map((o) => {
            const id = `${idPrefix}-${o.value}`
            return (
              <div
                key={o.value}
                className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-surface-hover"
              >
                <Checkbox
                  id={id}
                  boxSize="sm"
                  checked={selectedSet.has(o.value)}
                  onChange={() => toggle(o.value)}
                />
                <label
                  htmlFor={id}
                  className="flex-1 cursor-pointer select-none truncate text-sm text-fg"
                >
                  {o.label}
                </label>
              </div>
            )
          })
        )}
      </div>
    </>
  )
}
